import keys from 'ramda/src/keys'
import toPairs from 'ramda/src/toPairs'
import sort from 'ramda/src/sort'
import {
  ASSISTANCE_TYPE_LEGEND_ORDER,
  LEGEND_MAPPING,
  OUTCOME_MAPPING,
  AUTOMATION_RATE_LEGEND_ORDER,
  IGNORED_OUTCOMES
} from '@/utils/filter.constants'
import chartLabels from '@/utils/chart-labels'
import botModule from '@/store/modules/bot.module'

class RequestOutcomeMapper {

  mapOutcomeToLegend(outcome, chartType = 'automationRate') {
    return OUTCOME_MAPPING[chartType][outcome]
  }

  mapLegendsToOutcomes(legends, type = 'requestOutcomes') {
    const mappedLegends = []
    legends.forEach(legend => {
      mappedLegends.push(...LEGEND_MAPPING[type][legend])
    })
    return mappedLegends
  }

  getAllLegends(legendType = 'requestOutcomes') {
    return keys(LEGEND_MAPPING[legendType])
  }

  getIgnoreOutcomes(resolutionLegends, legendType) {
    return toPairs(LEGEND_MAPPING[legendType]).reduce((ignoredOutcomes, [legend, outcomes]) => {
      if (!resolutionLegends.includes(legend)) {
        ignoredOutcomes.push(...outcomes)
      }
      return ignoredOutcomes
    }, [])
  }

  getOutcomesByResolution(legend, chartType = 'assistanceType') {
    return toPairs(OUTCOME_MAPPING[chartType])
      .filter(outcomeEntry => outcomeEntry[1] === legend)
      .map(outcomeEntry => outcomeEntry[0])
  }

  getOutcomesByResolutions(resolutions, chartType = 'assistanceType') {
    const outcomes = []
    resolutions.forEach(resolution => {
      outcomes.push(...this.getOutcomesByResolution(resolution, chartType))
    })
    return outcomes
  }

  sortOutcomes(aggregatedRequestOutcomes, chartType = 'assistanceType') {
    const sortBy = chartType === 'assistanceType' ? ASSISTANCE_TYPE_LEGEND_ORDER : AUTOMATION_RATE_LEGEND_ORDER
    const sorter = (a, b) => sortBy[a.legendType] - sortBy[b.legendType]
    return sort(sorter, aggregatedRequestOutcomes)
  }

  /**
   * Convert an outcome collection into a map
   * @param {Array} outcomes - Collection of outcomes details (id, name).
   * @return {Object} outcomeMap - a map {} of id and their names
   */
  buildOutcomeMap(outcomes) {
    return Object.assign({}, ...outcomes.map(outcome => ({ [outcome.id]: outcome.name })))
  }

  /**
   * creates data set fulfilling apex chart requirements
   * @param {Array} requests - list of requests data in object
   * @param {String} chartType - automationRate, assistanceType, intentUsages, defines mapping of
   * legend names to outcomes
   * @param {String} aggregateBy - every request contains information about its resolution outcome, automation outcome,
   * and usages outcome, parameter defines which an outcome is used for aggregation
   * @return {Array} Array of { count, legendType }
   */
  aggregateChartData(requests, chartType, aggregateBy = 'resolution') {
    const outcomeSum = requests.reduce((map, request) => {
      const outcome = request[aggregateBy]
      const assistanceType = this.mapOutcomeToLegend(outcome, chartType)
      map.set(assistanceType, (map.get(assistanceType) || 0) + 1)

      return map
    }, new Map)

    return Array.from(outcomeSum, ([legendType, count]) => ({ legendType, count }))
  }

  /**
   * returns list of requests and extending them by information about their 1.intent usage outcome, 2.automation outcome
   * and 3.resolution outcome, each used for dedicated chart or table
   * @param {Array} requestOutcomes - list of requestOutcomes data in objects
   * @param {Object} outcomeMap - map of outcomes ID and name
   * @return {Array} list of {contact_id, id, intent_platform_id, outcome_created_on, outcome_id, resolution, automation, usages}
   */
  mapRequestOutcomes(requestOutcomes, outcomeMap) {
    const outcomes = Object.keys(OUTCOME_MAPPING.automationRate)
    const automationOutcomes = outcomes.filter(outcome => !IGNORED_OUTCOMES.includes(outcome))

    const requestOutcomesMap = {}
    const requests = []
    const allRequestOutcomesLength = requestOutcomes.length

    for (let i = 0; i < allRequestOutcomesLength; i++) {
      const requestOutcome = requestOutcomes[i]
      const outcome = outcomeMap[requestOutcome.outcome_id]
      if (!requestOutcomesMap[requestOutcome.id]) {
        requestOutcomesMap[requestOutcome.id] = [outcome]
      } else {
        requestOutcomesMap[requestOutcome.id].push(outcome)
      }

      if (requestOutcome.id !== requestOutcomes[i + 1]?.id) {
        const reversedRequestOutcomes = requestOutcomesMap[requestOutcome.id].reverse()
        requests.push({
          ...requestOutcome,
          resolution: outcome,
          automation: reversedRequestOutcomes.find(outcome => automationOutcomes.includes(outcome))
        })
      }
    }

    return requests
  }

  /**
   * returns list of a combination of contact and outcome, which are unique
   * @param {Array} requestOutcomes - list of requestOutcomes data in object
   * @param {String } uniqueBy - automation, usage, resolution
   * @return {Array} list of {contact_id, id, intent_platform_id, outcome_created_on, outcome_id}
   */
  mapContactOutcomes(requestOutcomes, uniqueBy = 'automation') {
    const seen = new Set()
    return requestOutcomes.filter(request_outcome => {
      const combinedKey = `${request_outcome.contact_id}|${request_outcome[uniqueBy]}`
      if (seen.has(combinedKey)) return false

      seen.add(combinedKey)
      return true
    })
  }

  /**
   * @param {Array} contacts - list of unique contacts data in object
   * @return {Number} a count of unique contacts
   */
  calculateContactTotal(contacts) {
    const contactIds = new Set(contacts.map(({ contact_id }) => contact_id))
    return contactIds.size
  }

  /**
   * @param {Array} requests - list of all customer requests
   * @return {Number} a count of eligible (automation outcome isn't in IGNORED_OUTCOMES list) customer requests for charts
   */
  calculateRequestTotal(requests) {
    const eligibleRequests = requests.filter(request => !!request.automation)
    return eligibleRequests.length
  }

  // automation rate area chart
  /**
   * creates data set fulfilling apex chart requirements
   * @param {Array} requests - list of requests data in object
   * @return {Array} Array of { count, legendType }
   */
  mapAutomationRateData(requests, isEmailBot = false) {
    const aggregatedAutomationData = this.aggregateChartData(requests, 'automationRate', isEmailBot ? 'resolution' : 'automation')
    return aggregatedAutomationData.filter(({ legendType }) => !!legendType)
  }

  // assistance type pie chart
  /**
   * creates data set fulfilling apex chart requirements
   * @param {Array} requests - list of requests data in object
   * @return {Array} Array of { count, legendType }
   */
  mapAssistanceTypeData(requests) {
    const aggregatedAutomationData = this.aggregateChartData(requests, 'assistanceType', 'resolution')
    return aggregatedAutomationData.filter(({ legendType }) => !!legendType)
  }

  // assistance resolution area chart
  /**
   * creates data set fulfilling apex chart requirements
   * @param {Array} requests - list of requests data in object
   * @param {String} dateFrom - starting date of filtered period
   * @param {String} dateTo - ending date of filtered period
   * @param {Number} timeGranularity - minutes, a time between two data-points
   * @return {Array} Array of { colorType, data: [timestamp, amount], legendType, name }
   */
  mapAssistanceResolutionIntervals(requests, dateFrom, dateTo, timeGranularity) {
    const timeGranularityMilliseconds = timeGranularity * 60000
    const from = +new Date(dateFrom)
    const to = +new Date(dateTo)

    const datapointCount = Math.round((to - from) / timeGranularityMilliseconds)
    const outcomeIntervals = []

    // start intervals array
    let intervalStart = from
    for (let datapoint = 0; datapoint <= datapointCount; datapoint++) {
      const intervalEnd = intervalStart + timeGranularityMilliseconds
      outcomeIntervals[datapoint] = {
        from: intervalStart, to: intervalEnd, outcomes: []
      }
      intervalStart = intervalEnd + 1
    }

    // map through request outcomes and assign them to each interval
    const requestsLength = requests.length
    for (let i = 0; i < requestsLength; i++) {
      const request = requests[i]
      const requestOutcomeTimestamp = +new Date(request.outcome_created_on)

      outcomeIntervals.forEach((outcomeInterval, i) => {
        if (requestOutcomeTimestamp >= outcomeIntervals[i].from &&
          requestOutcomeTimestamp < outcomeIntervals[i].to
        ) {
          outcomeIntervals[i].outcomes.push(request)
        }
      })
    }

    // aggregate request outcomes to legend categories (fullyAutomated, partiallyAutomated etc...)
    outcomeIntervals.forEach((outcomeInterval, i) => {
      outcomeIntervals[i].outcomes = this.aggregateChartData(
        outcomeIntervals[i].outcomes, 'assistanceType'
      )
    })
    return outcomeIntervals
  }

  /**
   * creates data set fulfilling apex chart requirements
   * @param {Array} outcomeIntervals - list of data-points in a format {from, to, outcomes: {}}
   * @return {Array} Array of { colorType, data: [timestamp, amount], legendType, name }
   */
  mapAssistanceResolutionsData(outcomeIntervals) {
    const flattenOutcomeIntervals = this._flatOutcomeIntervals(outcomeIntervals)

    const resolutions = new Map()
    flattenOutcomeIntervals.forEach(({ legendType, timestampFrom, timestampTo, count, index }) => {
      const data = [timestampFrom, count]

      if (!resolutions.has(legendType)) {
        resolutions.set(legendType, {
          colorType: chartLabels[legendType].color,
          name: chartLabels[legendType].label,
          legendType,
          data: [data]
        })
      } else {
        const resolution = resolutions.get(legendType)
        const datapointIndex = resolution.data.findIndex(datapoint => datapoint[0] === timestampFrom)

        if (datapointIndex === -1) {
          resolution.data.push(data)
        } else {
          const oldCount = resolution.data[datapointIndex][1]
          resolution.data[datapointIndex] = [timestampFrom, count + oldCount]
        }
      }
    })

    return Array.from(resolutions.values())
  }

  /**
   * creates an array of objects containing datapoint data, which can be later converted into data set
   * fulfilling apex chart requirements
   * @param {Array} outcomeIntervals - list of data-points in a format {from, to, outcomes: {}}
   * @return {Array} Array of { count, index, legendType, timestampFrom, timestampTo }
   */
  _flatOutcomeIntervals(outcomeIntervals) {
    const flattenOutcomeIntervals = []

    outcomeIntervals.forEach(({ outcomes, from, to }, index) => {
      const uniqueResolutionTypes = new Set(Object.values(OUTCOME_MAPPING.assistanceType))
      uniqueResolutionTypes.forEach(resolution => {
        flattenOutcomeIntervals.push({
          timestampFrom: from,
          timestampTo: to,
          count: outcomes.find(({ legendType }) => legendType === resolution)?.count || 0,
          legendType: resolution,
          index
        })
      })
    })

    return flattenOutcomeIntervals
  }

  // answer usage column chart and table
  /**
   * Group market variants of the same logical answer. Shared answers across a
   * multibot are keyed by multiBotItemId; standalone answers fall back to id.
   * @param {Array} answers
   * @return {Array} groups of { representative, variants, intentPlatformIds }
   */
  _groupAnswersByMultiBotItem(answers) {
    const groups = []
    const groupsByKey = new Map()

    answers.forEach(answer => {
      const key = answer.multiBotItemId || answer.id
      if (!groupsByKey.has(key)) {
        const group = {
          representative: answer,
          variants: [],
          intentPlatformIds: new Set()
        }
        groupsByKey.set(key, group)
        groups.push(group)
      }
      const group = groupsByKey.get(key)
      group.variants.push(answer)
      if (answer.intent?.platformId) {
        group.intentPlatformIds.add(answer.intent.platformId)
      }
    })

    return groups
  }

  /**
   * remap answers into intents and their request outcomes
   * @param {Array} answers - list of answers data in object
   * @param {Array} requests - list of requests data in object
   * @param {Boolean} includeAllOutcomes - for feature filtering by resolution: to align numbers in filter box and table
   * @return {Array} list of intents and their outcomes
   */
  mapAnswerUsages(answers, requests, includeAllOutcomes = false, isEmailBot = false) {
    const answerRequestOutcomeMap = new Map()
    const requestOutcomesLength = requests.length

    for (let i = 0; i < requestOutcomesLength; i++) {
      const request = requests[i]

      const intentPlatformId = request.intent_platform_id
      if (!answerRequestOutcomeMap.has(intentPlatformId)) {
        answerRequestOutcomeMap.set(intentPlatformId, [request])
      } else {
        answerRequestOutcomeMap.get(intentPlatformId).push(request)
      }
    }

    const groupedAnswers = this._groupAnswersByMultiBotItem(answers)

    return groupedAnswers.map(group => {
      const answerRequestOutcomes = []
      group.intentPlatformIds.forEach(platformId => {
        const requestsForIntent = answerRequestOutcomeMap.get(platformId)
        if (requestsForIntent) answerRequestOutcomes.push(...requestsForIntent)
      })
      const outcomes = this.aggregateChartData(
        answerRequestOutcomes,
        isEmailBot ? 'intentUsagesEmail' : 'intentUsages',
        isEmailBot ? 'resolution' : 'automation'
      )
      const representative = group.representative
      const answerUsage = {
        answer_id: representative.id,
        answer_name: representative.name,
        answerPublished: group.variants.some(({ published }) => published),
        multi_bot_item_id: representative.multiBotItemId || representative.id,
        answer_ids: group.variants.map(({ id, botId }) => ({ id, botId })),
        outcomes
      }

      if (includeAllOutcomes) {
        const usage = outcomes.reduce((sum, { count }) => sum + count, 0)
        return { ...answerUsage, usage }
      }

      const usage = outcomes.filter(({ legendType }) => !!legendType).reduce((sum, { count }) => sum + count, 0)
      return { ...answerUsage, usage }
    })
  }

  /**
   * filter requests not fitting into criteria dateFrom, dateTo, lastRequestOutcomes
   * @param {Array} requests - list of requests data in object
   * @param {String} dateFrom - starting date of filtered period
   * @param {String} dateTo - ending date of filtered period
   * @param {Array} lastRequestOutcomes - list of outcome names, for which request outcomes should be counted
   * @return {Array} list of intents and their outcomes
   */
  filterRequests(requests, dateFrom = null, dateTo = null, lastRequestOutcomes = []) {
    if (!dateFrom && !dateTo && !lastRequestOutcomes.length) return requests

    return requests.filter(request => {
      const outcomeDate = new Date(request.outcome_created_on)
      return (
        outcomeDate >= new Date(dateFrom) &&
        outcomeDate < new Date(dateTo) &&
        lastRequestOutcomes.includes(request.resolution) &&
        botModule.unknownIntentPlatformId() !== request.intent_platform_id
      )
    })
  }
}

export default new RequestOutcomeMapper()
